/**
 * g.webauthn — passkeys (WebAuthn) on the client. A thin wrapper over the
 * NATIVE navigator.credentials API: it converts the base64url fields the
 * server's options carry (challenge, ids) to the ArrayBuffers the browser
 * wants, runs the ceremony, and serialises the credential back to the JSON
 * the API verifies. No dependency (deliberately not @simplewebauthn/browser).
 *
 * Used by the login page ("Sign in with a passkey") and the profile's
 * Security block ("Add a passkey"). See api.md "Passkeys".
 *
 *   g.webauthn.supported()            → the browser can do WebAuthn.
 *   g.webauthn.platformAvailable()    → a platform authenticator is present
 *                                       (Touch ID / Windows Hello) — a promise.
 *   g.webauthn.register(optionsJSON)  → navigator.credentials.create, the
 *                                       result as JSON (post to register-verify).
 *   g.webauthn.authenticate(optsJSON) → navigator.credentials.get, the result
 *                                       as JSON (post to verify).
 * A user cancellation / no-credential rejects with a NotAllowedError — the
 * caller treats it as "not now", not a failure to report.
 */

function toBuffer(base64url) {
	const base64 = String(base64url).replace(/-/g, '+').replace(/_/g, '/');
	const padded = base64 + '==='.slice((base64.length + 3) % 4);
	const binary = atob(padded);
	const bytes = new Uint8Array(binary.length);
	for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
	return bytes.buffer;
}

function toBase64url(buffer) {
	const bytes = new Uint8Array(buffer);
	let binary = '';
	for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
	return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function supported() {
	return typeof window !== 'undefined' && !!window.PublicKeyCredential && typeof navigator !== 'undefined' && !!navigator.credentials;
}

async function platformAvailable() {
	if (!supported() || !window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable) return false;
	try { return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable(); } catch { return false; }
}

/** The PublicKeyCredential → the JSON shape the server verifies. Prefers the
 *  native toJSON() (recent browsers), falls back to a manual serialisation. */
function credentialToJSON(cred) {
	if (typeof cred.toJSON === 'function') return cred.toJSON();
	const response = cred.response;
	const out = {
		id: cred.id,
		rawId: toBase64url(cred.rawId),
		type: cred.type,
		clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
		authenticatorAttachment: cred.authenticatorAttachment || undefined,
		response: { clientDataJSON: toBase64url(response.clientDataJSON) },
	};
	if (response.attestationObject) {
		// a registration (create) result
		out.response.attestationObject = toBase64url(response.attestationObject);
		if (typeof response.getTransports === 'function') out.response.transports = response.getTransports();
		if (typeof response.getPublicKeyAlgorithm === 'function') out.response.publicKeyAlgorithm = response.getPublicKeyAlgorithm();
		if (typeof response.getAuthenticatorData === 'function') out.response.authenticatorData = toBase64url(response.getAuthenticatorData());
		if (typeof response.getPublicKey === 'function') { const pk = response.getPublicKey(); if (pk) out.response.publicKey = toBase64url(pk); }
	} else {
		// an authentication (get) result
		out.response.authenticatorData = toBase64url(response.authenticatorData);
		out.response.signature = toBase64url(response.signature);
		if (response.userHandle) out.response.userHandle = toBase64url(response.userHandle);
	}
	return out;
}

async function register(optionsJSON) {
	const publicKey = {
		...optionsJSON,
		challenge: toBuffer(optionsJSON.challenge),
		user: { ...optionsJSON.user, id: toBuffer(optionsJSON.user.id) },
		excludeCredentials: (optionsJSON.excludeCredentials || []).map((c) => ({ ...c, id: toBuffer(c.id) })),
	};
	const cred = await navigator.credentials.create({ publicKey });
	return credentialToJSON(cred);
}

async function authenticate(optionsJSON) {
	const publicKey = {
		...optionsJSON,
		challenge: toBuffer(optionsJSON.challenge),
		allowCredentials: (optionsJSON.allowCredentials || []).map((c) => ({ ...c, id: toBuffer(c.id) })),
	};
	const cred = await navigator.credentials.get({ publicKey });
	return credentialToJSON(cred);
}

export const webauthn = { supported, platformAvailable, register, authenticate, toBuffer, toBase64url };
